import { useFormik } from "formik";
import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocationStore, usePipedriveStore, usePopupStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import useProperty from "../../hooks/useProperty";
import { ClosableOrder } from "../../store/ClosableStore";
import { formatDate, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import editStyles from "../Edit.module.css";
import AddressInput from "../inputs/AddressInput";
import PipedriveCompanyInput from "../inputs/PipedriveCompanyInput";
import StringInput from "../inputs/StringInput";
import Modal from "../Modal";
import OnMapSpinner from "../spinners/OnMapSpinner";

function LocationCreate() {
    const { longitude, latitude } = useParams();
    const navigate = useNavigate();
    const navigateToCompany = useNavigateToCompany();
    const locationStore = useLocationStore();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const coordinates = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const { property, isLoading, fetchProperty } = useProperty(coordinates);
    const formik = useFormik({
        initialValues: LocationState.empty(),
        onSubmit: async (values) => {
            const createdLocationId = await locationStore.addLocation({
                pipedriveCompanyId: company!.id,
                regridId: property!.regridId,
                siteName: values.siteName!,
                address: values.address!,
                notes: values.notes!,
                property: property!,
            });

            navigateToCompany(company!.id);
            popupStore.openLocationPopup(createdLocationId);
        },
    });

    const company = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.name === formik.values.pipedriveCompanyName)).get(), [formik.values.pipedriveCompanyName]);

    const cancel = () => navigate(-1);
    useClosable(ClosableOrder.Medium, cancel);

    useEffect(() => {
        fetchProperty();
    }, [coordinates]);

    useEffect(() => {
        if (property) {
            formik.resetForm({
                values: {
                    ...LocationState.empty(),
                    siteName: `${property.city}, ${property.state}`,
                    address: property.address,
                },
            });
        }
    }, [property]);

    if (isLoading)
        return <OnMapSpinner />;

    if (!property)
        return null;

    return (
        <Modal midWidth header="Add Location" onClose={cancel}>
            <div className={editStyles.column}>
                <PipedriveCompanyInput formik={formik} label="COMPANY" name="pipedriveCompanyName" showNewLabel={false} />
                <StringInput formik={formik} label="SITE NAME" name="siteName" />
                <AddressInput formik={formik} label="ADDRESS" name="address" />
                <StringInput formik={formik} label="NOTES" name="notes" />
                <div className={commonStyles.row}>
                    <div className={commonStyles.key}>ZONING</div>
                    <div className={commonStyles.value}>{property.zoning}</div>
                </div>
                <div className={commonStyles.row}>
                    <div className={commonStyles.key}>BUILDING SIZE</div>
                    <div className={commonStyles.value}>{!!property.buildingSize && `${formatNumber(property.buildingSize)} Sq-Ft`}</div>
                </div>
                <div className={commonStyles.row}>
                    <div className={commonStyles.key}>LAND SIZE</div>
                    <div className={commonStyles.value}>{!!property.landSize && `${formatNumber(property.landSize)} Acres`}</div>
                </div>
                <div className={commonStyles.row}>
                    <div className={commonStyles.key}>OWNER</div>
                    <div className={commonStyles.value}>{property.ownerEntity}</div>
                </div>
                <div className={commonStyles.row}>
                    <div className={commonStyles.key}>DATE SOLD</div>
                    <div className={commonStyles.value}>{!!property.saleDate && formatDate(new Date(property.saleDate))}</div>
                </div>
                <div className={commonStyles.row}>
                    <div className={commonStyles.key}>SALE PRICE</div>
                    <div className={commonStyles.value}>{!!property.salePrice && formatPrice(property.salePrice)}</div>
                </div>
                <div className={commonStyles.row}>
                    <div className={commonStyles.key}>OWNER ADDRESS</div>
                    <div className={commonStyles.value}>{property.ownerAddress}</div>
                </div>
            </div>
            <div className={[commonStyles.modalControls, commonStyles.controlGroup].join(" ")}>
                <button className={commonStyles.secondaryButton} onClick={cancel}>Cancel</button>
                {company && formik.values.address && (
                    <button type="submit" className={commonStyles.primaryButton} onClick={() => formik.handleSubmit()}>Save</button>
                )}
            </div>
        </Modal>
    );
}

export default observer(LocationCreate);

export interface ILocationState {
    pipedriveCompanyName: string | null;
    siteName: string | null;
    address: string | null;
    notes: string | null;
}

class LocationState implements ILocationState {
    pipedriveCompanyName: string | null = null;
    siteName: string | null = null;
    address: string | null = null;
    notes: string | null = null;

    static empty(): LocationState {
        return new LocationState();
    }
}